/* eslint-disable no-nested-ternary */
/* eslint-disable arrow-body-style */
import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, IconButton, Box, Typography, Grid2 as Grid, Divider, Paper, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import { GLOBAL_DATE_FORMAT } from 'constant';
import ESign from 'components/ESign';
import { LoadingButton } from '@mui/lab';
import { useAssessmentForm } from './useAssessmentForm';
import usePermission from 'hooks/usePermission';
import useAuth from 'hooks/useAuth';
import { useReactToPrint } from 'react-to-print';
import { useGetClientLogoQuery } from 'store/services/client';
import ViewBilling from 'components/therapy/ViewBillingDetails';
import Spinner from 'ui-component/Spinner';

const AssessmentView = ({ open, onClose, data, onPrintClick, loading = false }) => {
    const { handleEsign, isSigning } = useAssessmentForm({ AssessmentId: data?.id, screenType: 'view' });
    const { isMaster, isSameAsMasterAdmin, isSuperAdmin } = usePermission();
    const { user, client } = useAuth();
    const [isPrintMode, setIsPrintMode] = useState(false);

    const { data: logo } = useGetClientLogoQuery({ ...(user?.client_id && { client_id: user.client_id }) }, { skip: !user?.client_id });

    const componentRef = useRef();
    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        onAfterPrint: () => {
            // Close print mode after printing
            setIsPrintMode(false);
        }
    });

    // Handle external print trigger
    useEffect(() => {
        if (onPrintClick === true && data) {
            // Enter print mode (renders content hidden)
            setIsPrintMode(true);
            // Wait for content to render, then print
            setTimeout(() => {
                handlePrint();
            }, 100);
        }
    }, [onPrintClick, data]);

    const [imageError, setImageError] = useState(false);

    const handleError = () => {
        setImageError(true);
    };

    useEffect(() => {
        setImageError(false);
    }, []);

    if (!data) return null;

    const InfoRow = ({ label, value, fullWidth = false }) => (
        <Grid size={{ xs: 12, sm: fullWidth ? 12 : 6 }}>
            <Box sx={{ display: 'flex', gap: 1.5, py: 1 }}>
                <Typography
                    sx={{
                        fontWeight: 600,
                        minWidth: 150,
                        color: '#212121'
                    }}
                >
                    {label}
                </Typography>
                <Typography sx={{ fontWeight: 500, color: 'text.secondary' }}>:</Typography>
                <Typography sx={{ flex: 1, color: '#424242' }}>{value || 'N/A'}</Typography>
            </Box>
        </Grid>
    );

    const formatDateTime = (value) => (value ? moment(value).format('MM/DD/YYYY - hh:mm a') : 'N/A');

    // The printable content component
    const PrintableContent = () => (
        <Paper ref={componentRef} sx={{ m: 0 }}>
            <Grid
                container
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                {/* Left spacer - hidden on print */}
                <Grid item xs sx={{ '@media print': { display: 'none' } }} />

                <Grid
                    xs
                    container
                    sx={{
                        justifyContent: 'flex-end',
                        '@media print': { display: 'none' }
                    }}
                >
                    {open && (
                        <>
                            <Grid>
                                <ViewBilling billingId={data?.billing_details} isMentalHealth />
                                <Button
                                    sx={{
                                        ml: 1
                                    }}
                                    variant="contained"
                                    onClick={handlePrint}
                                >
                                    Print
                                </Button>
                            </Grid>
                            <IconButton onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </>
                    )}
                </Grid>

                {/* Title */}
                <Grid
                    size={{ xs: 12, md: 12 }}
                    container
                    sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 2,
                        mr: 2,
                        ml: 2,

                        '@media print': {
                            width: '100%',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }
                    }}
                >
                    {/* LEFT — TITLE */}
                    <Grid sx={{ textAlign: 'left' }}>
                        <Typography variant="h3" component="span" sx={{ fontWeight: 500 }}>
                            Psychological Testing Details
                        </Typography>
                    </Grid>

                    {/* RIGHT — LOGO */}
                    <Grid>
                        {logo ? (
                            !imageError ? (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <img
                                        className="print-logo"
                                        src={logo}
                                        alt={client?.name}
                                        onError={handleError}
                                        style={{
                                            maxWidth: '220px',
                                            height: 'auto',
                                            borderRadius: '8px',
                                            objectFit: 'contain',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                            background: '#fff',
                                            padding: '6px'
                                        }}
                                    />
                                </Box>
                            ) : (
                                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                    {client?.name}
                                </Typography>
                            )
                        ) : (
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                {client?.name}
                            </Typography>
                        )}
                    </Grid>
                </Grid>
            </Grid>

            <Divider sx={{ width: '100%', my: 1 }} />
            <Grid container spacing={1} sx={{ padding: 2 }}>
                {/* BASIC DETAILS */}
                <InfoRow label="Patient" value={data?.patient?.name} />
                <InfoRow label="Location" value={data?.location?.name} />
                <InfoRow
                    label="Employee"
                    value={
                        data?.employee ? `${data.employee.name}${data.employee.credentials ? `, ${data.employee.credentials}` : ''}` : ''
                    }
                />
                <InfoRow label="Service" value={data?.service?.name} />

                <InfoRow
                    label="Diagnosis"
                    value={data?.diagnosis?.length ? `${data.diagnosis[0].code} - ${data.diagnosis[0].name}` : 'N/A'}
                />

                {/* DATE & TIME */}
                <Divider sx={{ width: '100%', my: 1 }} />

                <InfoRow label="Start Time" value={formatDateTime(data?.start_time)} />
                <InfoRow label="End Time" value={formatDateTime(data?.end_time)} />

                {/* OTHER DETAILS */}
                <Divider sx={{ width: '100%', my: 2 }} />

                <InfoRow label="Assessment Type" value={data?.assessment_type?.name} />
                <InfoRow label="Qualified" value={data?.is_qualified ? 'Yes' : 'No'} />

                <InfoRow label="Activity" value={data?.activity_type?.name} fullWidth />
                <InfoRow label="Notes" value={data?.notes || 'No notes available'} fullWidth />
            </Grid>

            {data?.is_full_signed && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <div className="sign-info">
                        <p>Signed by</p>
                        <p>
                            <b>
                                {data?.signed_therapist_emp?.name || ''} {data?.signed_therapist_emp?.credentials || ''}
                            </b>{' '}
                            on <b>{moment.utc(data?.therapist_sign_date).local().format(`${GLOBAL_DATE_FORMAT} hh:mm A`)}</b>
                        </p>
                    </div>
                </Box>
            )}
        </Paper>
    );

    if (loading) {
        return <Spinner />;
    }

    return (
        <>
            {/* Hidden print mode - renders content off-screen for printing */}
            {isPrintMode && !open && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: '-9999px',
                        top: 0
                    }}
                >
                    <PrintableContent />
                </Box>
            )}

            {/* Normal dialog mode */}
            {open && (
                <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                    <DialogContent sx={{ p: 0 }}>
                        <PrintableContent />
                        <Grid
                            container
                            spacing={1}
                            sx={{
                                mt: 1,
                                pl: 1,
                                justifyContent: 'flex-start'
                            }}
                        >
                            {!data?.is_full_signed && data?.employee?.id === user?.emp_id && (
                                <Grid>
                                    <ESign
                                        renderButton={(props) => (
                                            <LoadingButton loading={isSigning} variant="contained" color="secondary" {...props}>
                                                E-Sign
                                            </LoadingButton>
                                        )}
                                        onChange={(password) => handleEsign(password, data?.id)}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
};

export default AssessmentView;
